import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProjects, getProjectsForSeeker, getProjectDisplayName, type Project } from '@data/projects';
import { useAuth } from '@services/auth';
import { useAdminViewAs, getAdminEffectiveSeekerIdNum, getAdminEffectiveCompanyOrGardenerIdNum } from '../../lib/adminViewAs';
import { getStoredCompanyScopeMode, COMPANY_WORKSPACE_CHANGED_EVENT, getMyCompany } from '@services/companies';
import CompanyWorkspaceStrip from '../../shared/CompanyWorkspaceStrip';
import type { GanttTask } from '../../types/gantt';
import { getGardeners, type UserResponse } from '@services/users';
import { getCompanyGardeners } from '@services/gardeners';
import * as tasksAPI from '@services/tasks';
import type { TaskResponse } from '@services/tasks';
import TaskDetailModal from '../../components/TaskDetailModal';
import TaskLegalBadge from '../../components/TaskLegalBadge';
import '../../styles/workload.css';
import { useI18n } from '../../i18n';
import { getTaskLegalMeta } from '../../utils/taskLegalStatus';
import { toLocalDateString } from '../../utils/dateUtils';

const MS_PER_DAY = 1000 * 60 * 60 * 24;
const DAYS_PER_STEP = 7;
const DAY_COLUMN_WIDTH_PX = 50;

function startOfLocalDay(date: Date): Date {
	const normalized = new Date(date);
	normalized.setHours(0, 0, 0, 0);
	return normalized;
}

function endOfLocalDay(date: Date): Date {
	const normalized = new Date(date);
	normalized.setHours(23, 59, 59, 999);
	return normalized;
}

function toTaskLocalDateTime(input: string, useEndOfDay: boolean): string {
	// Keep plain YYYY-MM-DD values as local dates (no timezone conversion).
	if (/^\d{4}-\d{2}-\d{2}$/.test(input)) {
		return `${input}T${useEndOfDay ? '23:59:59.999' : '00:00:00.000'}`;
	}

	const parsed = new Date(input);
	if (Number.isNaN(parsed.getTime())) {
		const fallback = new Date();
		return `${toLocalDateString(fallback)}T${useEndOfDay ? '23:59:59.999' : '00:00:00.000'}`;
	}

	const normalized = useEndOfDay ? endOfLocalDay(parsed) : startOfLocalDay(parsed);
	const h = String(normalized.getHours()).padStart(2, '0');
	const m = String(normalized.getMinutes()).padStart(2, '0');
	const s = String(normalized.getSeconds()).padStart(2, '0');
	const ms = String(normalized.getMilliseconds()).padStart(3, '0');
	return `${toLocalDateString(normalized)}T${h}:${m}:${s}.${ms}`;
}

function getLocalDayNumber(date: Date): number {
	return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / MS_PER_DAY;
}

// Convert database task to GanttTask format
function dbTaskToGanttTask(dbTask: TaskResponse, project: Project): GanttTask {
	return {
		id: `db-${dbTask.id}`,
		name: dbTask.title,
		description: dbTask.description || undefined,
		startDate: dbTask.start_date ? toTaskLocalDateTime(dbTask.start_date, false) : toTaskLocalDateTime(toLocalDateString(new Date()), false),
		endDate: dbTask.end_date ? toTaskLocalDateTime(dbTask.end_date, true) : toTaskLocalDateTime(toLocalDateString(new Date()), true),
		duration: dbTask.duration || 1,
		progress: dbTask.progress || 0,
		status: dbTask.status || 'not_started',
		dependencies: dbTask.dependencies || [],
		isCritical: dbTask.is_critical || false,
		gardenerIds: dbTask.gardener_ids.map(id => String(id)),
		machineIds: dbTask.machine_ids || [],
		projectId: project.id,
		notes: dbTask.notes || undefined,
		legalSeasonalBanCandidate: Boolean(dbTask.legal_seasonal_ban_candidate),
		legalSeasonalBanReason: dbTask.legal_seasonal_ban_reason || undefined
	} as GanttTask;
}

export default function Schedule() {
	const { user } = useAuth();
	const { viewAsTarget } = useAdminViewAs();
	const { language } = useI18n();
	const copy = (en: string, de: string, hr: string) =>
		language === 'de' ? de : language === 'hr' ? hr : en;
	const locale = language === 'de' ? 'de-DE' : language === 'hr' ? 'hr-HR' : 'en-GB';
	const navigate = useNavigate();
	const [projects, setProjects] = useState<Project[]>([]);
	const [databaseTasks, setDatabaseTasks] = useState<TaskResponse[]>([]);
	const [gardeners, setGardeners] = useState<UserResponse[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [filterProject, setFilterProject] = useState<string>('all');
	const [filterGardener, setFilterGardener] = useState<string>('all');
	const [viewMode, setViewMode] = useState<'list' | 'workload'>('workload');
	const [selectedTask, setSelectedTask] = useState<{ task: GanttTask; project: Project } | null>(null);
	const [dateRange, setDateRange] = useState<{ start: Date; end: Date }>(() => {
		const today = new Date();
		const start = startOfLocalDay(today);
		start.setDate(today.getDate() - 7); // Start 7 days ago
		const end = endOfLocalDay(today);
		end.setDate(today.getDate() + 30); // End 30 days from now
		return { start, end };
	});
	const workloadScrollRef = useRef<HTMLDivElement>(null);
	const [workspaceListRev, setWorkspaceListRev] = useState(0);
	const [companyCountryCode, setCompanyCountryCode] = useState<string | null>(null);

	useEffect(() => {
		const onWorkspace = () => setWorkspaceListRev((n) => n + 1);
		window.addEventListener(COMPANY_WORKSPACE_CHANGED_EVENT, onWorkspace);
		return () => window.removeEventListener(COMPANY_WORKSPACE_CHANGED_EVENT, onWorkspace);
	}, []);

	const loadData = async () => {
		setIsLoading(true);
		try {
			const showAllCompanies = getStoredCompanyScopeMode() === 'all_companies';
			let loadedProjects: Project[];
			let loadedTasks: TaskResponse[] = [];

			const adminSeekerLens = user ? getAdminEffectiveSeekerIdNum(user, viewAsTarget) : null;
			const adminCoGardenerLens = user ? getAdminEffectiveCompanyOrGardenerIdNum(user, viewAsTarget) : null;

			if (user?.role === 'admin' && viewAsTarget && adminSeekerLens != null) {
				loadedProjects = (await getProjectsForSeeker(adminSeekerLens)) || [];
				if (loadedProjects.length > 0) {
					const projectTasks = await Promise.all(
						loadedProjects.map((project) =>
							tasksAPI.getTasksForProject(typeof project.id === 'string' ? parseInt(project.id, 10) : project.id)
						)
					);
					loadedTasks = projectTasks.flat();
					const uniqueTasks = new Map<number, TaskResponse>();
					loadedTasks.forEach((task) => {
						if (!uniqueTasks.has(task.id)) uniqueTasks.set(task.id, task);
					});
					loadedTasks = Array.from(uniqueTasks.values());
				}
			} else if (user?.role === 'admin' && viewAsTarget && adminCoGardenerLens != null) {
				const scope = showAllCompanies ? 'all_companies' : 'company';
				loadedProjects = await getProjects({
					requester_user_id: adminCoGardenerLens,
					company_scope: scope
				});
				loadedTasks = await tasksAPI.getTasks({
					requester_user_id: adminCoGardenerLens,
					company_scope: scope
				});
			} else if (user?.role === 'admin') {
				loadedProjects = await getProjects();
				loadedTasks = await tasksAPI.getTasks();
			} else if ((user?.role === 'company' || user?.role === 'gardener') && user?.id) {
				const requesterId = parseInt(String(user.id), 10);
				if (!isNaN(requesterId)) {
					const scope = showAllCompanies ? 'all_companies' : 'company';
					loadedProjects = await getProjects({
						requester_user_id: requesterId,
						company_scope: scope
					});
					loadedTasks = await tasksAPI.getTasks({
						requester_user_id: requesterId,
						company_scope: scope
					});
				} else {
					loadedProjects = [];
					loadedTasks = [];
				}
			} else {
				loadedProjects = await getProjects();
				loadedTasks = await tasksAPI.getTasks();
			}

			setProjects(loadedProjects);
			setDatabaseTasks(loadedTasks);

			// Load gardeners: company/gardener see only their company; admin sees all
			try {
				let apiGardeners: UserResponse[];
				if (user?.role === 'admin' && viewAsTarget && adminCoGardenerLens != null) {
					const companyGardeners = await getCompanyGardeners(adminCoGardenerLens);
					apiGardeners = companyGardeners.map((g) => ({
						id: g.id,
						name: g.display_name || g.name || g.email || '',
						email: g.email,
						role: g.role,
						company_name: g.company_name,
						created_at: '',
						updated_at: ''
					}));
				} else if (user?.role === 'company' || user?.role === 'gardener') {
					const userIdNum = typeof user.id === 'string' ? parseInt(user.id, 10) : user.id;
					if (!user?.id || isNaN(userIdNum)) {
						apiGardeners = [];
					} else {
						const companyGardeners = await getCompanyGardeners(userIdNum);
						apiGardeners = companyGardeners.map((g) => ({
							id: g.id,
							name: g.display_name || g.name || g.email || '',
							email: g.email,
							role: g.role,
							company_name: g.company_name,
							created_at: '',
							updated_at: ''
						}));
					}
				} else {
					apiGardeners = await getGardeners();
				}
				setGardeners(apiGardeners);
			} catch (err) {
				console.error('Failed to load gardeners:', err);
			}
		} catch (err) {
			console.error('Failed to load data:', err);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		loadData();
	}, [user, workspaceListRev, user?.activeCompanyId, viewAsTarget]);

	useEffect(() => {
		let cancelled = false;
		const lensCoG = user ? getAdminEffectiveCompanyOrGardenerIdNum(user, viewAsTarget) : null;
		const effectiveCompanyUserId =
			user?.role === 'admin' && lensCoG != null ? String(lensCoG) : user?.id ? String(user.id) : '';
		if (user?.role !== 'company' && user?.role !== 'gardener' && !(user?.role === 'admin' && lensCoG != null)) {
			setCompanyCountryCode(null);
			return;
		}
		if (!effectiveCompanyUserId) return;
		getMyCompany(effectiveCompanyUserId, user?.activeCompanyId ?? undefined)
			.then((company) => {
				if (!cancelled) setCompanyCountryCode(company?.company?.country_code || null);
			})
			.catch(() => {
				if (!cancelled) setCompanyCountryCode(null);
			});
		return () => {
			cancelled = true;
		};
	}, [user?.role, user?.activeCompanyId, user?.id, viewAsTarget]);

	// Update selectedTask when databaseTasks or projects change (after refresh)
	useEffect(() => {
		if (selectedTask && selectedTask.task.id.startsWith('db-')) {
			const taskId = parseInt(selectedTask.task.id.replace('db-', ''));
			const updatedDbTask = databaseTasks.find(t => t.id === taskId);
			if (updatedDbTask) {
				const project = projects.find(p => String(p.id) === String(updatedDbTask.project_id));
				if (project) {
					const updatedGanttTask = dbTaskToGanttTask(updatedDbTask, project);
					setSelectedTask({ task: updatedGanttTask, project });
				}
			}
		}
	}, [databaseTasks, projects]);

	// Use only database tasks (same as Tasks page) to avoid showing the same task twice
	// when it exists both in the DB and in project.ganttData from the plan.
	const allTasks = useMemo(() => {
		const tasks: Array<{ task: GanttTask; project: Project }> = [];
		const seenTaskIds = new Set<number>();

		databaseTasks.forEach(dbTask => {
			if (seenTaskIds.has(dbTask.id)) return;
			seenTaskIds.add(dbTask.id);

			if (dbTask.project_id) {
				const project = projects.find(p => String(p.id) === String(dbTask.project_id));
				if (project) {
					const ganttTask = dbTaskToGanttTask(dbTask, project);
					tasks.push({ task: ganttTask, project });
				}
			}
		});

		return tasks;
	}, [databaseTasks, projects]);

	// Filter tasks based on user role and filters
	const filteredTasks = useMemo(() => {
		return allTasks.filter(({ task, project }) => {
			// Apply project filter
			if (filterProject !== 'all' && project.id !== filterProject) return false;
			
			// Apply gardener filter
			if (filterGardener !== 'all') {
				const taskGardenerIds = task.gardenerIds || (task.gardenerId ? [task.gardenerId] : []);
				if (!taskGardenerIds.includes(filterGardener)) return false;
			}
			
			// Tasks are already filtered by project access in loadData
			// No need to filter by assignment here - gardeners see all tasks from their projects
			
			return true;
		});
	}, [allTasks, filterProject, filterGardener, user]);

	// Sort by start date
	const sortedTasks = useMemo(() => {
		return [...filteredTasks].sort((a, b) => {
			const dateA = new Date(a.task.startDate).getTime();
			const dateB = new Date(b.task.startDate).getTime();
			return dateA - dateB;
		});
	}, [filteredTasks]);

	const getStatusColor = (status?: string): string => {
		switch (status) {
			case 'completed': return 'success';
			case 'in_progress': return 'warning';
			case 'at_risk': return 'warning';
			case 'delayed': return 'danger';
			case 'blocked': return 'danger';
			default: return 'secondary';
		}
	};

	const getProgressMeta = (progress?: number): { color: 'secondary' | 'warning' | 'success'; value: number } => {
		const normalized = Math.max(0, Math.min(100, progress ?? 0));
		if (normalized >= 100) {
			return { color: 'success', value: 100 };
		}
		if (normalized > 0) {
			return { color: 'warning', value: normalized };
		}
		return { color: 'secondary', value: 0 };
	};

	const getGardenerName = (gardenerId?: string): string => {
		if (!gardenerId) return 'Unassigned';
		const gardener = gardeners.find(g => String(g.id) === gardenerId);
		return gardener?.name || 'Unknown';
	};

	// Calculate workload for each gardener
	const gardenerWorkload = useMemo(() => {
		const workload: Record<string, {
			gardener: UserResponse;
			tasks: Array<{ task: GanttTask; project: Project }>;
			dailyHours: Record<string, number>; // date string -> hours
			capacity: number; // hours per day (default 8)
		}> = {};

		// Initialize workload for each gardener
		gardeners.forEach(gardener => {
			workload[String(gardener.id)] = {
				gardener,
				tasks: [],
				dailyHours: {},
				capacity: 8 // Default 8 hours per day
			};
		});

		// Calculate workload per day for each gardener
		filteredTasks.forEach(({ task, project }) => {
			// Get gardener IDs - ensure they're strings for comparison
			const taskGardenerIds = task.gardenerIds 
				? task.gardenerIds.map(id => String(id))
				: (task.gardenerId ? [String(task.gardenerId)] : []);
			
			if (taskGardenerIds.length === 0) {
				// Skip tasks with no assigned gardeners
				return;
			}
			
			const startDate = new Date(task.startDate);
			startDate.setHours(0, 0, 0, 0);
			const endDate = new Date(task.endDate);
			endDate.setHours(23, 59, 59, 999);
			
			// Calculate actual duration in days (including start and end)
			const durationDays = Math.max(1, Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1);
			
			// Count weekdays in the task duration
			let weekdayCount = 0;
			for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
				const dayOfWeek = d.getDay();
				if (dayOfWeek !== 0 && dayOfWeek !== 6) {
					weekdayCount++;
				}
			}
			
			// Distribute 8 hours per task across weekdays only
			// If multiple gardeners, split the hours equally
			const hoursPerGardener = 8 / taskGardenerIds.length;
			const hoursPerWeekday = weekdayCount > 0 ? hoursPerGardener / weekdayCount : 0;

			taskGardenerIds.forEach(gardenerId => {
				// Ensure gardenerId is a string for lookup
				const gardenerIdStr = String(gardenerId);
				if (workload[gardenerIdStr]) {
					workload[gardenerIdStr].tasks.push({ task, project });
					
					// Distribute hours across task duration (only weekdays)
					for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
						const dayOfWeek = d.getDay();
						// Only count weekdays (Monday-Friday)
						if (dayOfWeek !== 0 && dayOfWeek !== 6) {
							const dateKey = toLocalDateString(d);
							if (!workload[gardenerIdStr].dailyHours[dateKey]) {
								workload[gardenerIdStr].dailyHours[dateKey] = 0;
							}
							workload[gardenerIdStr].dailyHours[dateKey] += hoursPerWeekday;
						}
					}
				}
			});
		});

		return workload;
	}, [filteredTasks, gardeners]);

	// Generate date range for timeline
	const timelineDates = useMemo(() => {
		const dates: Date[] = [];
		const current = new Date(dateRange.start);
		while (current <= dateRange.end) {
			dates.push(new Date(current));
			current.setDate(current.getDate() + 1);
		}
		return dates;
	}, [dateRange]);

	// Get date position on timeline
	const getDatePosition = (date: Date): number => {
		return getLocalDayNumber(date) - getLocalDayNumber(dateRange.start);
	};

	// Check if date is weekend
	const isWeekend = (date: Date): boolean => {
		const day = date.getDay();
		return day === 0 || day === 6;
	};

	// Get capacity color for a gardener on a specific date
	const getCapacityColor = (gardenerId: string, date: Date): { outer: string; inner?: string; percentage: number } => {
		const workload = gardenerWorkload[gardenerId];
		if (!workload) return { outer: '#e9ecef', percentage: 0 };

		const dateKey = toLocalDateString(date);
		const hours = workload.dailyHours[dateKey] || 0;
		const capacity = workload.capacity;
		const percentage = (hours / capacity) * 100;

		if (isWeekend(date)) {
			return { outer: '#f1f3f5', percentage: 0 }; // Grey for weekends
		}

		if (percentage >= 100) {
			return { outer: '#0d6efd', percentage: 100 }; // Blue = at capacity
		} else if (percentage > 0) {
			return { outer: '#b3d9ff', inner: '#0d6efd', percentage }; // Light blue with darker center
		} else {
			return { outer: '#e9ecef', percentage: 0 }; // Grey = no work
		}
	};

	// Navigate date range
	const navigateDateRange = (direction: 'prev' | 'next') => {
		const offset = direction === 'next' ? DAYS_PER_STEP : -DAYS_PER_STEP;
		
		setDateRange(prev => {
			const nextStart = new Date(prev.start);
			const nextEnd = new Date(prev.end);
			nextStart.setDate(nextStart.getDate() + offset);
			nextEnd.setDate(nextEnd.getDate() + offset);
			return {
				start: startOfLocalDay(nextStart),
				end: endOfLocalDay(nextEnd)
			};
		});
	};

	// Get tasks for a specific gardener and date
	const getTasksForDate = (gardenerId: string, date: Date): Array<{ task: GanttTask; project: Project }> => {
		const workload = gardenerWorkload[gardenerId];
		if (!workload) return [];

		return workload.tasks.filter(({ task }) => {
			const day = startOfLocalDay(date);
			const startDate = startOfLocalDay(new Date(task.startDate));
			const endDate = endOfLocalDay(new Date(task.endDate));
			return day >= startDate && day <= endDate;
		});
	};

	if (isLoading) {
		return (
			<div className="container-fluid py-4">
				<div className="text-center py-5">
					<div className="spinner-border text-primary" role="status">
						<span className="visually-hidden">Loading schedule...</span>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="container-fluid py-4">
			<CompanyWorkspaceStrip />
			<div className="d-flex justify-content-between align-items-center mb-4">
				<div>
					<h1 className="h3 mb-1">{copy('Schedule', 'Zeitplan', 'Raspored')}</h1>
					<p className="text-muted mb-0">{copy('View workload and task assignments', 'Arbeitslast und Aufgabenzuweisungen anzeigen', 'Prikaži opterećenje i dodjele zadataka')}</p>
				</div>
				<div className="d-flex gap-2">
					<button
						className={`btn btn-sm ${viewMode === 'workload' ? 'btn-success' : 'btn-outline-secondary'}`}
						onClick={() => setViewMode('workload')}
					>
						<i className="bi bi-calendar3-range me-1"></i>
						{copy('Workload', 'Auslastung', 'Opterećenje')}
					</button>
					<button
						className={`btn btn-sm ${viewMode === 'list' ? 'btn-success' : 'btn-outline-secondary'}`}
						onClick={() => setViewMode('list')}
					>
						<i className="bi bi-list-ul me-1"></i>
						{copy('List', 'Liste', 'Popis')}
					</button>
				</div>
			</div>

			{/* Filters */}
			<div className="card mb-4">
				<div className="card-body">
					<div className="row g-3">
						<div className="col-md-6">
							<label className="form-label">{copy('Filter by Project', 'Filtern nach Projekt', 'Filtriraj po projektu')}</label>
							<select
								className="form-select"
								value={filterProject}
								onChange={(e) => setFilterProject(e.target.value)}
							>
								<option value="all">{copy('All Projects', 'Alle Projekte', 'Svi projekti')}</option>
								{projects.map(project => (
									<option key={project.id} value={project.id}>
										{getProjectDisplayName(project)}
									</option>
								))}
							</select>
						</div>
						<div className="col-md-6">
							<label className="form-label">{copy('Filter by Gardener', 'Filtern nach Gärtner', 'Filtriraj po vrtlaru')}</label>
							<select
								className="form-select"
								value={filterGardener}
								onChange={(e) => setFilterGardener(e.target.value)}
							>
								<option value="all">{copy('All Gardeners', 'Alle Gärtner', 'Svi vrtlari')}</option>
								{gardeners.map(gardener => (
									<option key={gardener.id} value={String(gardener.id)}>
										{gardener.name}
									</option>
								))}
							</select>
						</div>
					</div>
				</div>
			</div>

			{viewMode === 'workload' ? (
				<div className="workload-view">
					{/* Date Range Controls */}
					<div className="card mb-3">
						<div className="card-body py-2">
							<div className="d-flex justify-content-between align-items-center">
								<div
									className="d-flex justify-content-between align-items-center"
									style={{ minWidth: '280px' }}
								>
									<button
										className="btn btn-sm btn-outline-secondary flex-shrink-0"
										onClick={() => navigateDateRange('prev')}
									>
										<i className="bi bi-chevron-left"></i>
									</button>
									<span className="text-muted small text-center mx-2 flex-grow-1">
										{dateRange.start.toLocaleDateString(locale, { day: 'numeric', month: 'short' })} -
										{dateRange.end.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })}
									</span>
									<button
										className="btn btn-sm btn-outline-secondary flex-shrink-0"
										onClick={() => navigateDateRange('next')}
									>
										<i className="bi bi-chevron-right"></i>
									</button>
								</div>
								<div className="text-muted small">
									{copy('Daily timeline', 'Tägliche Zeitleiste', 'Dnevna vremenska crta')}
								</div>
							</div>
						</div>
					</div>

					{/* Workload View */}
					<div className="card">
						<div className="workload-container" ref={workloadScrollRef}>
							{/* Date Header */}
							<div className="workload-header">
								<div className="workload-resource-header">
									<div className="fw-semibold">{copy('Resource', 'Ressource', 'Resurs')}</div>
								</div>
								<div className="workload-timeline-header">
									{timelineDates.map((date) => {
										const dayOfWeek = date.getDay();
										const dayName = [
											copy('Sun', 'So', 'Ned'),
											copy('Mon', 'Mo', 'Pon'),
											copy('Tue', 'Di', 'Uto'),
											copy('Wed', 'Mi', 'Sri'),
											copy('Thu', 'Do', 'Čet'),
											copy('Fri', 'Fr', 'Pet'),
											copy('Sat', 'Sa', 'Sub')
										][dayOfWeek];
										const isWeekendDay = isWeekend(date);
										
										return (
											<div
												key={date.toISOString()}
												className={`workload-date-cell ${isWeekendDay ? 'weekend' : ''}`}
											>
												<div className="workload-date-day">{dayName}</div>
												<div className="workload-date-label">
													{date.toLocaleDateString(locale, { day: 'numeric', month: 'short' })}
												</div>
											</div>
										);
									})}
								</div>
							</div>

							{/* Gardener Rows */}
							<div className="workload-body">
								{Object.values(gardenerWorkload).length === 0 ? (
									<div className="workload-row">
										<div className="workload-resource-cell">
											<div className="text-muted">{copy('No gardeners found', 'Keine Gärtner gefunden', 'Nisu pronađeni vrtlari')}</div>
										</div>
										<div className="workload-timeline-cell"></div>
									</div>
								) : Object.values(gardenerWorkload)
									.filter(w => filterGardener === 'all' || String(w.gardener.id) === filterGardener)
									.length === 0 ? (
									<div className="workload-row">
										<div className="workload-resource-cell">
											<div className="text-muted">{copy('No gardeners match the filter', 'Keine Gärtner entsprechen dem Filter', 'Nijedan vrtlar ne odgovara filtru')}</div>
										</div>
										<div className="workload-timeline-cell"></div>
									</div>
								) : Object.values(gardenerWorkload)
									.filter(w => filterGardener === 'all' || String(w.gardener.id) === filterGardener)
									.map(workload => {
										// Calculate maximum number of overlapping tasks to determine row height
										const calculateMaxLayers = () => {
											const taskLayers: Array<Array<{ task: GanttTask; project: Project }>> = [];
											
											workload.tasks.forEach(({ task, project }) => {
												const taskStart = new Date(task.startDate);
												taskStart.setHours(0, 0, 0, 0);
												const taskEnd = new Date(task.endDate);
												taskEnd.setHours(23, 59, 59, 999);
												
												let placed = false;
												for (let layer = 0; layer < taskLayers.length; layer++) {
													const hasOverlap = taskLayers[layer].some(({ task: existingTask }) => {
														const existingStart = new Date(existingTask.startDate);
														const existingEnd = new Date(existingTask.endDate);
														return !(taskEnd < existingStart || taskStart > existingEnd);
													});
													
													if (!hasOverlap) {
														taskLayers[layer].push({ task, project });
														placed = true;
														break;
													}
												}
												
												if (!placed) {
													taskLayers.push([{ task, project }]);
												}
											});
											
											return taskLayers.length;
										};
										
										const maxLayers = calculateMaxLayers();
										// Base height 80px + 28px per additional layer, minimum 80px
										const rowHeight = Math.max(80, 80 + (maxLayers - 1) * 28);
										
										return (
										<div 
											key={workload.gardener.id} 
											className="workload-row"
											style={{ minHeight: `${rowHeight}px` }}
										>
											<div className="workload-resource-cell">
												<div className="fw-semibold">{workload.gardener.name}</div>
												<div className="small text-muted">
													{workload.tasks.length} {copy(
														workload.tasks.length !== 1 ? 'tasks' : 'task',
														workload.tasks.length !== 1 ? 'Aufgaben' : 'Aufgabe',
														workload.tasks.length !== 1 ? 'zadataka' : 'zadatak'
													)}
												</div>
											</div>
											<div className="workload-timeline-cell" style={{ position: 'relative' }}>
												{/* Render date cells */}
												{timelineDates.map((date) => {
													const isWeekendDay = isWeekend(date);
													const tasksOnDate = getTasksForDate(String(workload.gardener.id), date);
													
													return (
														<div
															key={date.toISOString()}
															className={`workload-date-cell ${isWeekendDay ? 'weekend' : ''}`}
															title={`${date.toLocaleDateString()}${tasksOnDate.length > 0 ? `: ${tasksOnDate.length} task(s)` : ''}`}
														>
														</div>
													);
												})}
												
												{/* Render task bars that span across multiple dates - positioned below bubbles */}
												{(() => {
													// Calculate stacking for overlapping tasks
													const taskLayers: Array<Array<{ task: GanttTask; project: Project }>> = [];
													
													workload.tasks.forEach(({ task, project }) => {
														const taskStart = new Date(task.startDate);
														taskStart.setHours(0, 0, 0, 0);
														const taskEnd = new Date(task.endDate);
														taskEnd.setHours(23, 59, 59, 999);
														
														// Find the first layer where this task doesn't overlap
														let placed = false;
														for (let layer = 0; layer < taskLayers.length; layer++) {
															const hasOverlap = taskLayers[layer].some(({ task: existingTask }) => {
																const existingStart = new Date(existingTask.startDate);
																const existingEnd = new Date(existingTask.endDate);
																return !(taskEnd < existingStart || taskStart > existingEnd);
															});
															
															if (!hasOverlap) {
																taskLayers[layer].push({ task, project });
																placed = true;
																break;
															}
														}
														
														if (!placed) {
															taskLayers.push([{ task, project }]);
														}
													});
													
													// Flatten layers and render - ensure tasks only belong to this gardener
													return taskLayers.flatMap((layer, layerIndex) =>
														layer.map(({ task, project }) => {
															// Double-check: ensure this task is assigned to the current gardener
															const taskGardenerIds = task.gardenerIds || (task.gardenerId ? [String(task.gardenerId)] : []);
															const gardenerIdStr = String(workload.gardener.id);
															if (!taskGardenerIds.includes(gardenerIdStr)) {
																return null; // Skip if task doesn't belong to this gardener
															}
															
															const taskStart = new Date(task.startDate);
															taskStart.setHours(0, 0, 0, 0);
															const taskEnd = new Date(task.endDate);
															taskEnd.setHours(23, 59, 59, 999);
															
															// Only render if task overlaps with visible date range
															const rangeStart = new Date(dateRange.start);
															rangeStart.setHours(0, 0, 0, 0);
															const rangeEnd = new Date(dateRange.end);
															rangeEnd.setHours(23, 59, 59, 999);
															
															if (taskEnd < rangeStart || taskStart > rangeEnd) {
																return null;
															}
															
															const visibleStart = taskStart < rangeStart ? rangeStart : taskStart;
															const visibleEnd = taskEnd > rangeEnd ? rangeEnd : taskEnd;
															
															const startPos = getDatePosition(visibleStart);
															const endPos = getDatePosition(visibleEnd);
															const width = (endPos - startPos + 1) * DAY_COLUMN_WIDTH_PX;
															const left = startPos * DAY_COLUMN_WIDTH_PX;
															const legalMeta = getTaskLegalMeta(task, companyCountryCode);
															
															const statusColor = getStatusColor(task.status);
															const color = statusColor === 'success' ? '#198754' :
																statusColor === 'warning' ? '#ffc107' :
																statusColor === 'danger' ? '#dc3545' : '#6c757d';
															
															// Position task bar below capacity bubbles, stacking overlapping tasks
															const topOffset = 8 + (layerIndex * 28); // Start near top of row, stack vertically
															
															return (
																<div
																	key={`${workload.gardener.id}-${task.id}`}
																	className="workload-task-bar-span"
																	style={{
																		left: `${left}px`,
																		width: `${width}px`,
																		backgroundColor: color,
																		top: `${topOffset}px`,
																		maxWidth: `${width}px`,
																	}}
																	title={`${task.name} (${taskStart.toLocaleDateString()} - ${taskEnd.toLocaleDateString()}) - ${task.progress || 0}%${legalMeta ? ` - ${legalMeta.reason}` : ''}`}
																	onClick={(e) => {
																		e.stopPropagation();
																		setSelectedTask({ task, project });
																	}}
																>
																	<span className="workload-task-bar-label">
																		{task.name}{legalMeta ? ' [!]' : ''} {task.progress !== undefined && task.progress > 0 && (
																			<span className="workload-task-progress">({task.progress}%)</span>
																		)}
																	</span>
																</div>
															);
														})
													);
												})()}
											</div>
										</div>
										);
									})}
							</div>
						</div>

						{/* Legend */}
						<div className="card-footer bg-light">
							<div className="d-flex gap-4 align-items-center flex-wrap">
								<div className="d-flex align-items-center gap-2">
									<div className="workload-legend-item" style={{ backgroundColor: '#198754' }}></div>
									<span className="small">{copy('Completed', 'Abgeschlossen', 'Završeno')}</span>
								</div>
								<div className="d-flex align-items-center gap-2">
									<div className="workload-legend-item" style={{ backgroundColor: '#ffc107' }}></div>
									<span className="small">{copy('In Progress', 'In Bearbeitung', 'U tijeku')}</span>
								</div>
								<div className="d-flex align-items-center gap-2">
									<div className="workload-legend-item" style={{ backgroundColor: '#dc3545' }}></div>
									<span className="small">{copy('Delayed/Blocked', 'Verzögert/Blockiert', 'Odgođeno/Blokirano')}</span>
								</div>
								<div className="d-flex align-items-center gap-2">
									<div className="workload-legend-item" style={{ backgroundColor: '#f1f3f5' }}></div>
									<span className="small">{copy('Weekend', 'Wochenende', 'Vikend')}</span>
								</div>
							</div>
						</div>
					</div>
				</div>
			) : (
				<div className="card">
					<div className="card-body">
						{sortedTasks.length === 0 ? (
							<div className="alert alert-info">
								<strong>{copy('No tasks found.', 'Keine Aufgaben gefunden.', 'Nema pronađenih zadataka.')}</strong>
								<p className="mb-0 mt-2">
									{allTasks.length === 0
										? copy('No timeline tasks available. Tasks will appear here once projects have timeline data.', 'Keine Timeline-Aufgaben verfügbar. Aufgaben erscheinen hier, sobald Projekte Zeitplandaten haben.', 'Nema dostupnih zadataka vremenske crte. Zadaci će se pojaviti kad projekti imaju podatke vremenske crte.')
										: copy('No tasks match the current filters.', 'Keine Aufgaben entsprechen den aktuellen Filtern.', 'Nijedan zadatak ne odgovara trenutnim filterima.')}
								</p>
							</div>
						) : (
							<div className="table-responsive">
								<table className="table table-hover">
									<thead>
										<tr>
											<th>Task</th>
											<th>Project</th>
											<th>Start Date</th>
											<th>End Date</th>
											<th>Days</th>
											<th>Assigned To</th>
											<th>Progress</th>
										</tr>
									</thead>
									<tbody>
										{sortedTasks.map(({ task, project }) => {
											const taskGardenerIds = task.gardenerIds || (task.gardenerId ? [task.gardenerId] : []);
											const assignedGardeners = taskGardenerIds.map(id => getGardenerName(id)).filter(name => name !== 'Unassigned');
											const progressMeta = getProgressMeta(task.progress);
											const legalMeta = getTaskLegalMeta(task, companyCountryCode);
											
											return (
												<tr
													key={`${project.id}-${task.id}`}
													style={{ cursor: 'pointer' }}
													onClick={() => setSelectedTask({ task, project })}
												>
													<td>
														<div className="d-flex align-items-center gap-2 flex-wrap">
															<div className="fw-semibold">{task.name}</div>
															<TaskLegalBadge legalMeta={legalMeta} copy={copy} />
														</div>
														{task.description && (
															<div className="small text-muted">{task.description}</div>
														)}
													</td>
													<td>{getProjectDisplayName(project)}</td>
													<td>{new Date(task.startDate).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })}</td>
													<td>{new Date(task.endDate).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })}</td>
													<td>{task.duration} day{task.duration !== 1 ? 's' : ''}</td>
													<td>
														{assignedGardeners.length > 0 ? (
															<div className="small">
																{assignedGardeners.join(', ')}
															</div>
														) : (
															<span className="text-muted small">Unassigned</span>
														)}
													</td>
													<td>
														<div className="d-flex align-items-center gap-2">
															<div className="progress" style={{ width: '130px', height: '20px' }}>
																<div
																	className={`progress-bar bg-${progressMeta.color}`}
																	role="progressbar"
																	style={{ width: `${progressMeta.value}%` }}
																	aria-valuenow={progressMeta.value}
																	aria-valuemin={0}
																	aria-valuemax={100}
																>
																	{progressMeta.value}%
																</div>
															</div>
														</div>
													</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
						)}
					</div>
				</div>
			)}

			{/* Task Detail Modal */}
			{selectedTask && (
				<TaskDetailModal
					task={selectedTask.task}
					project={selectedTask.project}
					companyCountryCode={companyCountryCode}
					gardeners={gardeners}
					onClose={() => setSelectedTask(null)}
					onUpdate={() => {
						loadData();
					}}
				/>
			)}
		</div>
	);
}

